import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  StarIcon,
  CheckIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  BoltIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import AuthenticatedNavigation from '../components/common/AuthenticatedNavigation';
import { useAuth } from '../hooks/useAuth';

interface DealOption {
  id: string;
  commitmentAmount: number;
  discountRate: number;
  advanceDrawAmount: number;
  maxVWAPPurchase: number;
  maxRapidPurchase: number;
  vwapPercent?: number;
  rapidPercent?: number;
  // Origination fee inputs (added in Deal Options step)
  applyingFor?: 'self' | 'other';
  originationFeePercent?: number; // UI percent (e.g., 2 for 2%)
  originationFeeRate?: number; // decimal (e.g., 0.02)
  isRecommended?: boolean;
  features: string[];
  targetUseCase: string;
}

interface DealOptionsProps {
  eligibilityResults: any;
  onUpdateState: (updates: any) => void;
}

const DealOptions: React.FC<DealOptionsProps> = ({ eligibilityResults, onUpdateState }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [options, setOptions] = useState<DealOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<DealOption | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showFloorPriceModal, setShowFloorPriceModal] = useState(false);
  const [applyingFor, setApplyingFor] = useState<'self' | 'other'>('self');
  const [originationFeePercent, setOriginationFeePercent] = useState<number>(2.0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const roundCommitmentAmount = (amount: number): number => {
    // Round based on the magnitude of the amount
    if (amount >= 1000000000) { // >= $1B
      return Math.round(amount / 100000000) * 100000000; // Round to nearest $100M
    } else if (amount >= 500000000) { // >= $500M
      return Math.round(amount / 50000000) * 50000000; // Round to nearest $50M
    } else if (amount >= 100000000) { // >= $100M
      return Math.round(amount / 10000000) * 10000000; // Round to nearest $10M
    } else if (amount >= 30000000) { // >= $30M
      // For amounts like $37.5M, prefer rounding to $35M or $40M
      const millions = amount / 1000000;
      if (millions % 10 === 7.5) {
        // Round 37.5 → 35, 47.5 → 45, etc.
        return Math.floor(millions / 5) * 5 * 1000000;
      }
      return Math.round(millions / 5) * 5 * 1000000;
    } else if (amount >= 10000000) { // >= $10M
      // For amounts like $12.5M, round to nearest $5M (which gives us $15M)
      return Math.round(amount / 5000000) * 5000000;
    } else {
      return Math.round(amount / 1000000) * 1000000; // Round to nearest $1M
    }
  };

  const handleSelectOption = (option: DealOption) => {
    // If clicking on already selected option, unselect it
    if (selectedOption?.id === option.id) {
      setSelectedOption(null);
    } else {
      setSelectedOption(option);
    }
  };

  const handleProceed = () => {
    if (selectedOption) {
      const sanitizedPercent = Number.isFinite(originationFeePercent) ? originationFeePercent : 0;
      const originationFeeRate = applyingFor === 'other' ? Math.max(0, sanitizedPercent) / 100 : 0;

      onUpdateState({
        selectedCommitment: {
          ...selectedOption,
          applyingFor,
          originationFeePercent: applyingFor === 'other' ? Math.max(0, sanitizedPercent) : 0,
          originationFeeRate
        }
      });
      navigate('/advance-draw');
    }
  };

  useEffect(() => {
    // Generate deal options based on eligibility results
    setTimeout(() => {
      const maxCommit = eligibilityResults?.maxFacilitySize || 10000000;
      const baseDiscountRate = eligibilityResults?.data?.underwriting?.discountRate || 0.025;
      const fiveDayAvgVolume = eligibilityResults?.data?.volumeData?.avgVolume5Day || eligibilityResults?.data?.stockData?.fiveDayVolume || 1000000;
      const maxVWAPVolumePercentage = eligibilityResults?.data?.underwriting?.vwapPercent || 0.5;
      
      // Round discount rates to nearest half percent
      const roundToHalfPercent = (rate: number) => {
        const percentage = rate * 100;
        return Math.round(percentage * 2) / 2;
      };
      
      // Generate 4 commitment options with tiered discount rates
      const dealOptions: DealOption[] = [
        {
          id: '1',
          commitmentAmount: roundCommitmentAmount(maxCommit * 0.25),
          discountRate: roundToHalfPercent(baseDiscountRate + 0.015), // Base + 1.5%
          advanceDrawAmount: roundCommitmentAmount(maxCommit * 0.25) * 0.01,
          maxVWAPPurchase: Math.min(roundCommitmentAmount(maxCommit * 0.25) * 0.1, fiveDayAvgVolume * maxVWAPVolumePercentage),
          maxRapidPurchase: Math.min(roundCommitmentAmount(maxCommit * 0.25) * 0.1, fiveDayAvgVolume * 0.2),
          vwapPercent: maxVWAPVolumePercentage,
          rapidPercent: 0.2,
          features: [
            `Advance Draw up to ${formatCurrency(roundCommitmentAmount(maxCommit * 0.25) * 0.01)}`,
            'Slightly higher discount for smaller commitment, ideal for those not seeking primary capital access',
            'Lower perceived dilution in public disclosures'
          ],
          targetUseCase: 'Reserve facility for favorable market conditions',
          isRecommended: false
        },
        {
          id: '2',
          commitmentAmount: roundCommitmentAmount(maxCommit * 0.5),
          discountRate: roundToHalfPercent(baseDiscountRate + 0.01), // Base + 1.0%
          advanceDrawAmount: roundCommitmentAmount(maxCommit * 0.5) * 0.01,
          maxVWAPPurchase: Math.min(roundCommitmentAmount(maxCommit * 0.5) * 0.1, fiveDayAvgVolume * maxVWAPVolumePercentage),
          maxRapidPurchase: Math.min(roundCommitmentAmount(maxCommit * 0.5) * 0.1, fiveDayAvgVolume * 0.2),
          vwapPercent: maxVWAPVolumePercentage,
          rapidPercent: 0.2,
          features: [
            `Advance Draw up to ${formatCurrency(roundCommitmentAmount(maxCommit * 0.5) * 0.01)}`,
            'Balanced discount rate for moderate commitment size',
            'Flexibility between reserve capital and active use'
          ],
          targetUseCase: 'Balanced approach for capital flexibility',
          isRecommended: false
        },
        {
          id: '3',
          commitmentAmount: roundCommitmentAmount(maxCommit * 0.75),
          discountRate: roundToHalfPercent(baseDiscountRate + 0.005), // Base + 0.5%
          advanceDrawAmount: roundCommitmentAmount(maxCommit * 0.75) * 0.01,
          maxVWAPPurchase: Math.min(roundCommitmentAmount(maxCommit * 0.75) * 0.1, fiveDayAvgVolume * maxVWAPVolumePercentage),
          maxRapidPurchase: Math.min(roundCommitmentAmount(maxCommit * 0.75) * 0.1, fiveDayAvgVolume * 0.2),
          vwapPercent: maxVWAPVolumePercentage,
          rapidPercent: 0.2,
          features: [
            `Advance Draw up to ${formatCurrency(roundCommitmentAmount(maxCommit * 0.75) * 0.01)}`,
            'Lower discount on larger commitment, suitable for regular capital access',
            'Higher advance draw for immediate capital needs'
          ],
          targetUseCase: 'Active capital source with significant flexibility',
          isRecommended: true
        },
        {
          id: '4',
          commitmentAmount: maxCommit,
          discountRate: roundToHalfPercent(baseDiscountRate), // Base rate
          advanceDrawAmount: maxCommit * 0.01,
          maxVWAPPurchase: Math.min(maxCommit * 0.1, fiveDayAvgVolume * maxVWAPVolumePercentage),
          maxRapidPurchase: Math.min(maxCommit * 0.1, fiveDayAvgVolume * 0.2),
          vwapPercent: maxVWAPVolumePercentage,
          rapidPercent: 0.2,
          features: [
            `Advance Draw up to ${formatCurrency(maxCommit * 0.01)}`,
            'Lowest discount rate for maximum commitment, ideal for primary capital access',
            'Maximum flexibility across all exercise types'
          ],
          targetUseCase: 'Primary source of growth capital',
          isRecommended: false
        }
      ];
      setOptions(dealOptions);
      setIsLoading(false);
    }, 1000);
  }, [eligibilityResults]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Matching Landing Page */}
      <header className="bg-white border-b border-gray-200">
        {/* Top Utility Bar */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-end items-center h-10 text-sm">
              {/* <a href="#demo" className="text-gray-700 hover:text-navy-700 ml-6">Schedule a Demo</a> */}
              <a href="#support" className="text-gray-700 hover:text-navy-700 ml-6">Customer Service</a>
              <button className="ml-6 text-navy-700 font-medium hover:text-navy-900">Login</button>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            {/* Logo */}
            <AuthenticatedNavigation className="flex items-center cursor-pointer">
              <img src="/24lp-logo-primary.png" alt="Twenty Four Capital" className="h-20 w-auto" />
            </AuthenticatedNavigation>
            
            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/#how-it-works" className="text-gray-700 hover:text-navy-700 font-medium">How it Works</a>
              <a href="/#platform-features" className="text-gray-700 hover:text-navy-700 font-medium">Platform Features</a>
              <a href="/#why-us" className="text-gray-700 hover:text-navy-700 font-medium">Why Choose</a>
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/')}
              className="bg-orange-500 text-white px-6 py-2.5 rounded-md font-medium hover:bg-orange-600 transition-colors"
            >
              {isAuthenticated ? 'Dashboard' : 'Home'}
            </button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Select Your Facility Size
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose a commitment amount based on your capital strategy and market perception considerations
            </p>
            {eligibilityResults?.companyName && (
              <div className="mt-4 inline-flex items-center bg-green-50 px-4 py-2 rounded-full">
                <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800 font-medium">
                  {eligibilityResults.companyName} qualifies for facilities up to {formatCurrency(eligibilityResults.maxFacilitySize)}
                </span>
              </div>
            )}
          </motion.div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center">
                <svg className="animate-spin h-8 w-8 text-orange-500 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-lg text-gray-600">Calculating your options...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Key Information Banner */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-gradient-to-r from-navy-700 to-navy-800 text-white rounded-xl p-6 mb-8"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                      <ShieldCheckIcon className="h-6 w-6 mr-2" />
                      Automatic At-the-Market Price Adjustment
                    </h3>
                    <p className="text-sm opacity-90">
                      Your discount rate applies only until the issuances reach the Exchange Cap (19.99% of Shares Outstanding). Once reached, our system automatically waives the discount and sets a floor price at the current market price—no prior shareholder approval needed and automated compliance tracking.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowFloorPriceModal(true)}
                    className="ml-4 text-white hover:text-orange-300 transition-colors"
                  >
                    <InformationCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </motion.div>

              {/* Deal Options Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {options.map((option, index) => (
                  <motion.div
                    key={option.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`relative rounded-xl border-2 transition-all cursor-pointer mt-6 min-h-[600px] flex flex-col ${
                      selectedOption?.id === option.id
                        ? 'border-orange-500 shadow-xl'
                        : option.isRecommended && !selectedOption
                        ? 'border-navy-700 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
                    }`}
                    onClick={() => handleSelectOption(option)}
                  >
                    {option.isRecommended && !selectedOption && (
                      <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 z-10">
                        <div className="bg-navy-700 text-white px-5 py-1 rounded-full text-sm font-semibold flex items-center whitespace-nowrap">
                          <StarIcon className="h-4 w-4 mr-1" />
                          Most Popular
                        </div>
                      </div>
                    )}

                    <div className="p-6 h-full flex flex-col">
                      {/* Commitment Amount */}
                      <div className="text-center mb-4">
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">
                          {formatCurrency(option.commitmentAmount)}
                        </h3>
                        <p className="text-sm text-gray-600">Commitment Amount</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Estimated Shares: {formatNumber(Math.round(option.commitmentAmount / (eligibilityResults?.data?.stockData?.price || 1)))}
                        </p>
                      </div>

                      {/* Use Case */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-4">
                        <p className="text-xs font-medium text-gray-700 text-center">{option.targetUseCase}</p>
                      </div>

                      {/* Key Metrics */}
                      <div className="space-y-3 mb-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Discount Rate</span>
                          <span className="font-semibold text-gray-900">{option.discountRate}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Advance Draw</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(option.advanceDrawAmount)}
                          </span>
                        </div>
                      </div>

                      {/* Features - flex-grow to push button to bottom */}
                      <div className="space-y-2 mb-4 flex-grow">
                        {option.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start">
                            <CheckIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Select Button - always at bottom */}
                      <button
                        className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all duration-200 ${
                          selectedOption?.id === option.id
                            ? 'bg-orange-500 text-white shadow-lg'
                            : option.isRecommended && !selectedOption
                            ? 'bg-navy-700 text-white hover:bg-navy-800 shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {selectedOption?.id === option.id ? '✓ Selected' : 'Select'}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Exercise Types Explanation */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-white rounded-xl shadow-lg p-8 mb-8"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Three Ways to Access Your Capital
                </h3>
                
                <div className="grid md:grid-cols-3 gap-6">
                  {/* VWAP Purchase */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-3">
                      <ChartBarIcon className="h-8 w-8 text-navy-700 mr-3" />
                      <h4 className="font-semibold text-gray-900">VWAP Purchase</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      For larger capital needs over a 3-day valuation period
                    </p>
                    <div className="bg-gray-50 rounded p-3">
                      <p className="text-xs font-medium text-gray-700">Maximum per notice:</p>
                      <p className="text-xs text-gray-600 mt-1">
                        Lower of {(eligibilityResults?.data?.underwriting?.vwapPercent * 100 || 50)}% of 5-day avg volume OR 10% of commitment
                      </p>
                    </div>
                  </div>

                  {/* Rapid Purchase */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-3">
                      <BoltIcon className="h-8 w-8 text-orange-500 mr-3" />
                      <h4 className="font-semibold text-gray-900">Rapid Purchase</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      For smaller amounts within a single trading session
                    </p>
                    <div className="bg-gray-50 rounded p-3">
                      <p className="text-xs font-medium text-gray-700">Maximum per notice:</p>
                      <p className="text-xs text-gray-600 mt-1">
                        Lower of 20% of 5-day avg volume OR 10% of commitment
                      </p>
                    </div>
                  </div>

                  {/* Advance Draw */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-3">
                      <CurrencyDollarIcon className="h-8 w-8 text-green-600 mr-3" />
                      <h4 className="font-semibold text-gray-900">Advance Draw</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      For immediate capital or bridge financing needs
                    </p>
                    <div className="bg-gray-50 rounded p-3">
                      <p className="text-xs font-medium text-gray-700">Maximum advance:</p>
                      <p className="text-xs text-gray-600 mt-1">
                        1% of commitment amount with 10% OID, Available as quickly as today!
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Selected Option Details */}
              {selectedOption && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-8"
                >
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Selected Facility: {formatCurrency(selectedOption.commitmentAmount)}
                  </h4>
                  <p className="text-xs text-gray-500 -mt-2 mb-3">
                    Estimated Shares: {formatNumber(Math.round(selectedOption.commitmentAmount / (eligibilityResults?.data?.stockData?.price || 1)))}
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-gray-600 font-medium mb-1">Maximum VWAP Purchase:</p>
                      <p className="text-xs text-gray-700 mb-2">
                        The lower of:
                      </p>
                      <ul className="text-xs text-gray-600 space-y-1 ml-3">
                        <li>• {formatCurrency(selectedOption.commitmentAmount * 0.1)} (10% of commitment)</li>
                        <li>• {(eligibilityResults?.data?.underwriting?.vwapPercent * 100 || 50)}% of 5-day avg volume</li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-gray-600 font-medium mb-1">Maximum Rapid Purchase:</p>
                      <p className="text-xs text-gray-700 mb-2">
                        The lower of:
                      </p>
                      <ul className="text-xs text-gray-600 space-y-1 ml-3">
                        <li>• {formatCurrency(selectedOption.commitmentAmount * 0.1)} (10% of commitment)</li>
                        <li>• 20% of 5-day avg volume</li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-gray-600 font-medium mb-1">Available Advance Draw:</p>
                      <p className="text-xs text-gray-700 mb-1">
                        1% of commitment amount
                      </p>
                      <p className="font-semibold text-gray-900">
                        = {formatCurrency(selectedOption.advanceDrawAmount)}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Subject to 10% OID, Available as quickly as today!
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-gray-600 font-medium mb-1">Your Discount Rate:</p>
                      <p className="font-semibold text-gray-900">
                        {selectedOption.discountRate}%
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Applies until 19.99% exchange cap is reached
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Origination Fee Selection */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-white border border-gray-200 rounded-xl p-6 mb-6"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Origination Fee</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Are you applying for your own company or on behalf of someone else?
                </p>

                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => setApplyingFor('self')}
                    className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                      applyingFor === 'self'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-gray-900">For my company</p>
                    <p className="text-xs text-gray-600">No origination fee (0%)</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setApplyingFor('other')}
                    className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                      applyingFor === 'other'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-gray-900">For someone else</p>
                    <p className="text-xs text-gray-600">Enter an origination fee percentage</p>
                  </button>
                </div>

                {applyingFor === 'other' && (
                  <div className="grid md:grid-cols-2 gap-4 items-end">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Origination fee (% of gross proceeds)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={25}
                          step={0.1}
                          value={Number.isFinite(originationFeePercent) ? originationFeePercent : 0}
                          onChange={(e) => setOriginationFeePercent(Number(e.target.value))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                      </div>
                   
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-700">Current selection:</p>
                      <p className="text-sm text-gray-900 mt-1">
                        {Math.max(0, Number.isFinite(originationFeePercent) ? originationFeePercent : 0).toFixed(2)}% origination fee
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <button
                  onClick={handleProceed}
                  disabled={
                    !selectedOption ||
                    (applyingFor === 'other' && !Number.isFinite(originationFeePercent))
                  }
                  className="flex-1 bg-orange-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  Continue to Advance Draw Selection
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </button>
                <button
                  onClick={() => navigate('/eligibility-success')}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Back
                </button>
              </motion.div>

              {/* Help Text */}
              <p className="text-center text-gray-600 mt-6">
                Questions about facility sizing? <button className="text-orange-500 hover:text-orange-600 font-medium">Schedule a consultation</button> with our capital markets team
              </p>
            </>
          )}
        </div>
      </section>

      {/* Floor Price Modal */}
      {showFloorPriceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Understanding the Automatic At-the-Market Price Adjustment
            </h3>
            
            <div className="space-y-4 text-gray-600">
              <p>
                This innovative feature protects both you and your shareholders while maintaining compliance with exchange rules.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">How it works:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Our system tracks every share issuance and whether it's at a discount to market price</li>
                  <li>We monitor progress toward the 19.99% exchange cap in real-time</li>
                  <li>Once 19.99% of outstanding shares have been issued at a discount, the system automatically:</li>
                  <ul className="list-disc list-inside ml-4 mt-1">
                    <li>Waives the discount rate</li>
                    <li>Sets the minimum price at the current market price</li>
                    <li>Continues facility availability without shareholder approval</li>
                  </ul>
                </ol>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Benefits:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
                  <li>No need for shareholder meetings or proxy statements</li>
                  <li>Facility remains active beyond typical exchange limitations</li>
                  <li>Automatic compliance with exchange rules</li>
                  <li>Highly competitive terms compared to traditional facilities</li>
                </ul>
              </div>
            </div>
            
            <button
              onClick={() => setShowFloorPriceModal(false)}
              className="mt-6 w-full bg-orange-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
            >
              Got it
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DealOptions; 
